import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import Signature from 'apps/Pdf/components/Signature/Signature';
import Loading from 'apps/Pdf/components/common/Loading';
import P from 'apps/Pdf/components/common/P';
import { ContractPhysicalData, ContractLegalData } from 'stores/workflowStores/workflowPersonalDataStore/model';
import PersonalData from 'apps/Pdf/templates/MainContract/components/PersonalData';
import CompanyData from 'apps/Pdf/templates/MainContract/components/CompanyData';
import H2 from 'apps/Pdf/components/common/H2';
import PageBreak from 'apps/Pdf/components/common/PageBreak';

import { PersonType } from 'enums';
import useColorStyles from 'apps/Pdf/styles/colors';
import useGeneralStyles from 'apps/Pdf/styles/general';
import { ContractAmendmentContent } from './ContractAmendmentInterface';
import { PdfCategory } from '../Interfaces';

const ContractAmendment = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const pdfContent = usePdfContent<ContractAmendmentContent>({ templateType: TPL_CONTRACT_AMENDMENT, category: PdfCategory.REQUEST, ...params });
  const data = pdfContent?.data ? pdfContent.data : ({} as ContractAmendmentContent);
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });
  const legal = pdfContent?.data && data.clientData.personType === PersonType.LEGAL;

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Dodatek rámcové smlouvy o poskytování investičních služeb</Title>

      <H2>Zákazník:</H2>

      {legal ? (
        <>
          <CompanyData data={data.clientData as ContractLegalData} />
          <H2>Zástupce zákazníka:</H2>
          <PersonalData data={(data.clientData as ContractLegalData).representative} />
        </>
      ) : (
        <PersonalData data={data.clientData as ContractPhysicalData} />
      )}

      <P>
        (dále jen „<strong>Zákazník</strong>“)
      </P>

      <P>
        Zákazník má zájem o službu spočívající v poskytování hlavní investiční služby přijímání a předávání pokynů
        týkajících se investičních nástrojů nabízených v rámci služeb WOOD RI a společnost WOOD RI je připravena mu tuto
        službu poskytnout.
      </P>

      <P>
        S ohledem na výše uvedené WOOD RI a Zákazník uzavírají Dodatek k Rámcové smlouvě, kterým se do Rámcové smlouvy
        přidává část č. III v níže uvedeném znění:
      </P>

      <Title>III. SMLOUVA O OBSTARÁNÍ A ZPROSTŘEDKOVÁNÍ KOUPĚ A PRODEJE INVESTIČNÍCH NÁSTROJŮ</Title>

      <P>
        v souladu s ustanovením § 2455 a násl. zákona č. 89/2012 Sb., občanský zákoník, v platném a účinném znění (dále
        jen "<strong>Komisionářská smlouva</strong>")
      </P>
      <ol className={styles.regularText}>
        <li>
          Předmětem Komisionářské smlouvy je závazek WOOD RI koupit nebo prodat Investiční nástroj pro Zákazníka, popř.
          obstarat pro něj koupi nebo prodej Investičních nástrojů, nebo vyvíjet činnost směřující ke koupi nebo prodeji
          Investičních nástrojů podle závazných Pokynů Zákazníka na straně jedné a závazek Zákazníka zaplatit za
          poskytnuté služby WOOD RI odměnu dle Sazebníku služeb.
        </li>
        <li>
          Nákup nebo prodej Investičních nástrojů bude WOOD RI provádět na základě závazných písemných nebo ústních
          Pokynů Zákazníka. Tyto Pokyny musí být určité a srozumitelné.
        </li>
        <li>
          Zákazníci využívající Internetovou aplikaci jsou oprávněni podávat Pokyny zásadně prostřednictvím Internetové
          aplikace. WOOD RI není povinna přijmout Pokyn podaný jinak než prostřednictvím Internetové aplikace.
        </li>
        <li>
          WOOD RI bude přijímat Pokyny k obstarání koupě nebo prodeje jen těch Investičních nástrojů, které jsou
          zobrazeny v Internetové aplikaci.
        </li>
        <li>
          Pokyny přijaté prostřednictvím Internetové aplikace budou realizovány v následujícím Obchodním cyklu, jedná-li
          se o Pokyn týkající se Investičního nástroje obchodovaného na Regulovaném trhu
        </li>
        <li>
          Ostatní pokyny přijaté prostřednictvím Internetové aplikace budou realizovány v souladu s emisními podmínkami
          daného Investičního nástroje.
        </li>
        <li>
          WOOD RI je oprávněna nákup nebo prodej Investičního nástroje uskutečnit i ze/do svého majetku nebo nákupem
          Investičních nástrojů od jiných svých Zákazníků.
        </li>
        <li>
          Další práva a povinnosti Zákazníka a WOOD RI vyplývající z Komisionářské smlouvy jsou stanoveny v Podmínkách.
        </li>
        <li>Komisionářská smlouva nabývá účinnosti ke dni podpisu Rámcové smlouvy.</li>
      </ol>

      <P>
        Ostatní ustanovení Rámcové smlouvy zůstávají beze změny. Pojmy uvedené v Dodatku s velkým počátečním písmenem
        jsou vysvětleny v Podmínkách. Na důkaz souhlasu s obsahem tohoto Dodatku připojují Smluvní strany své podpisy a
        zároveň prohlašují, že tento Dodatek byl uzavřen z jejich svobodné a vážné vůle.
      </P>

      <PageBreak />

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_CONTRACT_AMENDMENT: TemplateType = 'contractAmendment';

export { TPL_CONTRACT_AMENDMENT, ContractAmendment };
